import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { initFcm } from "../lib/fcm";
import { toast } from "sonner";

/**
 * Login-time notification opt-in — shown every sign-in session until the user
 * grants permission. A real button click gives browsers the user gesture they
 * need to show the native permission dialog.
 */
export default function NotificationPrompt({ user, backendUrl }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && "Notification" in window;
  const denied = supported && Notification.permission === "denied";

  useEffect(() => {
    if (!user || !supported) return;
    if (Notification.permission === "granted") return;
    const key = `icaard_notif_prompt_${user.id}`;
    if (sessionStorage.getItem(key)) return;
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, [user, supported]);

  // Auto-hide after 12s so the prompt never permanently blocks content near the bottom nav.
  useEffect(() => {
    if (!show || !user) return;
    const t = setTimeout(() => {
      sessionStorage.setItem(`icaard_notif_prompt_${user.id}`, "1");
      setShow(false);
    }, 12000);
    return () => clearTimeout(t);
  }, [show, user]);

  if (!show || !user) return null;

  const dismiss = () => {
    sessionStorage.setItem(`icaard_notif_prompt_${user.id}`, "1");
    setShow(false);
  };

  const enable = async () => {
    setBusy(true);
    try {
      const r = await initFcm({ apiBase: backendUrl, getAuthToken: () => localStorage.getItem("icard_token") });
      if (r?.token && r.pushReady === false) toast.info("This device is registered. iCaard server push delivery still needs setup.");
      else if (r?.token) toast.success("Notifications enabled 🎉");
      else if (r?.reason === "denied") toast.error("Notifications are blocked. enable them in your browser settings.");
      else toast.info("Could not enable notifications on this device.");
    } catch (_) {
      toast.error("Could not enable notifications.");
    }
    setBusy(false);
    dismiss();
  };

  return (
    <div className="pointer-events-none fixed bottom-[calc(5.75rem+env(safe-area-inset-bottom))] left-1/2 z-30 w-[min(22rem,calc(100vw-1.5rem))] -translate-x-1/2 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[21rem] sm:translate-x-0" data-testid="notification-prompt">
      <div role="region" aria-label="Notification settings" className="pointer-events-auto overflow-hidden rounded-[1.35rem] border border-[#33856C]/70 bg-[#10231F]/[.97] p-3.5 text-white shadow-[0_18px_55px_rgba(4,20,16,.38)] backdrop-blur-xl sm:p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-2xl border border-[#72D1A8]/25 bg-gradient-to-br from-[#1A8061]/35 to-[#D8A83F]/15 text-[#8CE0B8] shadow-inner">
            <Bell size={19} strokeWidth={2.1} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="text-[15px] font-extrabold leading-tight tracking-[-.01em] text-white">Turn on notifications</div>
            <p className="mt-1 text-[13px] leading-[1.45] text-[#D1E2DA]">
              {denied
                ? "Notifications are off in your browser. You can allow them in browser settings."
                : "Get helpful updates about your tickets, bookings and payments."}
            </p>
          </div>
          <button type="button" onClick={dismiss} className="-mr-1 -mt-1 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full text-[#B7C9C1] transition hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[#F0C45B]" aria-label="Dismiss notification prompt" data-testid="notif-dismiss-btn">
            <X size={17} />
          </button>
        </div>
        <div className="mt-3 flex items-center gap-2 pl-[3.25rem]">
          {!denied && (
            <button type="button" data-testid="notif-allow-btn" onClick={enable} disabled={busy}
                    className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-[#14815F] px-3 py-2 text-[13px] font-extrabold text-white shadow-[0_5px_14px_rgba(20,129,95,.25)] transition hover:bg-[#19966F] disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F0C45B]">
              {busy ? "Turning on…" : "Allow alerts"}
            </button>
          )}
          <button type="button" data-testid="notif-later-btn" onClick={dismiss}
                  className="inline-flex min-h-11 items-center justify-center rounded-xl px-3 text-[13px] font-bold text-[#D1E2DA] transition hover:bg-white/[.08] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F0C45B]">
            {denied ? "Got it" : "Maybe later"}
          </button>
        </div>
      </div>
    </div>
  );
}
